import {
  useState,
  useRef,
  useLayoutEffect,
  Dispatch,
  SetStateAction,
  RefObject,
  SyntheticEvent,
  ReactNode,
} from "react";
import styled, { CSSProperties } from "styled-components/macro";
import classNames from "classnames";
import { BORDER_RADIUS } from "../vars";
import useStackContext, {
  StackContextProvider,
} from "../hooks/use-stack-context";
import { Menu } from "./Menu";
import Portal from "./Portal";
import useFocusedElement from "../hooks/use-focused-element";

type PositionNames = "top" | "left" | "bottom" | "right";
type PositionFn = (rect: DOMRect) => number;
type Position = Partial<Record<PositionNames, PositionFn>>;

type Shared = {
  isShowing: boolean;
  show: (e?: SyntheticEvent) => void;
  hide: (e?: SyntheticEvent) => void;
};

export type OverlayTrigger = (
  props: Shared & {
    ref: RefObject<any>;
  }
) => ReactNode;

export type OverlayContent = (props: Shared) => ReactNode;

export default function Overlay({
  isShowing,
  setShowing,
  trigger,
  content,
  border,
  rect: providedRect,
  position,
  onShow,
  onHide,
  targetRef: givenTargetRef,
  contentMinWidth = "300px",
  contentMaxWidth = "500px",
}: {
  trigger: OverlayTrigger;
  content: OverlayContent;
  isShowing?: boolean;
  setShowing?: Dispatch<SetStateAction<boolean>>;
  border?: boolean;
  onShow?: () => void;
  onHide?: () => void;
  targetRef?: RefObject<HTMLElement>;
  rect?: DOMRect;
  position?: Position;
  contentMinWidth?: string;
  contentMaxWidth?: string;
}) {
  const handledState = useState(false);
  const [contentPosition, setContentPosition] = useState<CSSProperties>({
    position: "absolute",
  });
  const contentRef = useRef<HTMLDivElement>(null);
  let targetRef = useRef<HTMLElement>(null);
  if (givenTargetRef) targetRef = givenTargetRef;
  const zIndex = useStackContext();
  const getFocusedElement = useFocusedElement();

  if (isShowing === undefined || setShowing === undefined) {
    isShowing = handledState[0];
    setShowing = handledState[1];
  }

  const isRaw = !position;

  const show = (e?: SyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (onShow) onShow();

    setShowing!(true);

    window.requestAnimationFrame(() => {
      if (!contentRef.current) return;

      // only focus on the content box if the trigger still
      // has focus
      if (targetRef.current === getFocusedElement()) {
        contentRef.current.focus();
      }
    });
  };

  const hide = (e?: SyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (onHide) onHide();

    setShowing!(false);

    window.requestAnimationFrame(() => {
      if (
        targetRef.current &&
        targetRef.current.tagName !== "INPUT" &&
        !targetRef.current?.hasAttribute("data-reach-tooltip-trigger")
      )
        targetRef.current.focus();
    });
  };

  const getPosition: () => CSSProperties = () => {
    if (!targetRef.current) return {};
    const rect = providedRect || targetRef.current.getBoundingClientRect();

    const contentRect = contentRef.current
      ? contentRef.current.getBoundingClientRect()
      : { width: 0, height: 0 };

    const keyMap: Record<string, () => number> = {
      top: () => rect.top,
      left: () => rect.left,
      bottom: () => window.innerHeight - rect.bottom,
      right: () => window.innerWidth - rect.right,
    };

    const constraints: Record<PositionNames, (v: number) => number> = {
      top: (val) => Math.min(window.innerHeight - contentRect.height, val),
      left: (val) => Math.min(window.innerWidth - contentRect.width, val),
      bottom: (val) => Math.min(window.innerHeight - contentRect.height, val),
      right: (val) => Math.min(window.innerWidth - contentRect.width, val),
    };

    const result = !position
      ? {}
      : Object.fromEntries(
          Object.entries(position).map(([key, fn]) => [
            key,
            constraints[key as PositionNames](fn!(rect) + keyMap[key]()),
          ])
        );

    return { position: "absolute", ...result };
  };

  useLayoutEffect(() => {
    if (!isShowing) return;
    const fix = () => {
      const position = getPosition();
      setContentPosition((p) => {
        if (
          p.top === position.top &&
          p.left === position.left &&
          p.right === position.right &&
          p.bottom === position.bottom
        )
          return p;
        return position;
      });
    };

    const interval = setInterval(fix, 300);

    fix();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowing]);

  return (
    <>
      {trigger({ isShowing, show, hide, ref: targetRef })}
      {isShowing && (
        <Portal>
          <Container
            style={{ zIndex }}
            onClick={hide}
            role="button"
            className={classNames({ raw: isRaw, border })}
            contentMinWidth={contentMinWidth}
            contentMaxWidth={contentMaxWidth}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Escape") hide(e);
              }}
              role="presentation"
              style={contentPosition}
            >
              <div role="dialog" ref={contentRef} tabIndex={-1}>
                <StackContextProvider>
                  {content({ isShowing, show, hide })}
                </StackContextProvider>
              </div>
            </div>
          </Container>
        </Portal>
      )}
    </>
  );
}

const Container = styled.div<{
  contentMinWidth?: string;
  contentMaxWidth?: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  &.raw {
    background: rgba(0, 0, 0, 0.2);
  }
  &.raw > div:first-child {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    transform: translate(-50%, -50%);
    min-width: ${(p) => p.contentMinWidth};
    max-width: ${(p) => p.contentMaxWidth};
    z-index: 2;
    border-radius: ${BORDER_RADIUS};
  }
  &.raw > :first-child,
  &.border > :first-child {
    border-radius: ${BORDER_RADIUS};
    background: white;
  }
  &.border > :first-child {
    box-shadow: 0 0 0px 1px rgba(0, 0, 0, 0.1), 0 3px 6px rgba(0, 0, 0, 0.2);
  }
  & > [role="presentation"] > [role="dialog"] > * > ${Menu} {
    & > * {
      &:first-child {
        border-top-left-radius: ${BORDER_RADIUS};
        border-top-right-radius: ${BORDER_RADIUS};
      }
      &:last-child {
        border-bottom-left-radius: ${BORDER_RADIUS};
        border-bottom-right-radius: ${BORDER_RADIUS};
      }
    }
  }
`;
